import { GET_SHOP_DATA_QUERY, GetShopDataResponse, mapShopData } from 'hooks/shopify/queries/get-shop-data.graphql';
import { shopifyGraphQL } from 'hooks/shopify/shopify-graphql';
import { ShopifyShopData } from 'models/ShopifyShopData';
import useSWR, { mutate } from 'swr';
import swrConfig from 'utils/swrConfig';

export const SHOP_DATA_SWR_KEY = 'shopData';

/**
 * Fetches the shop's currency, country and billing plan straight from the Shopify Admin
 * GraphQL API. Usable outside React (thunks, services) — prefer `preloadShopData` so the
 * result also lands in the cache `useShopData` reads.
 */
export async function fetchShopData(): Promise<ShopifyShopData | undefined> {
    const response = await shopifyGraphQL<GetShopDataResponse>(GET_SHOP_DATA_QUERY);
    return mapShopData(response);
}

/**
 * Fetches the shop data and seeds the SWR cache with it, so components mounting later read
 * the bootstrapped value instead of firing a second request.
 */
export async function preloadShopData(): Promise<ShopifyShopData | undefined> {
    const shopData = await fetchShopData();
    await mutate(SHOP_DATA_SWR_KEY, shopData, { revalidate: false });
    return shopData;
}

export const useShopData = () => {
    const {
        data,
        error,
        isLoading,
        mutate: refetch,
    } = useSWR<ShopifyShopData>(SHOP_DATA_SWR_KEY, fetchShopData, swrConfig);

    return { data, error, isLoading, refetch };
};
